import { type ReactNode, useCallback, useEffect } from "react";
import {
  setTrackingAxiosInstance,
  setExitFlushEnricher,
} from "@b2bportal/core-tracking/batcher";
import type { TrackingPropertiesType } from "@hopper-b2b/types";
import {
  ACCEPT_LANGUAGE_HEADER,
  getAcceptLanguagesFromLocale,
  getDeviceData,
  getUserDeviceData,
  useNavigate,
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
} from "@hopper-b2b/utilities";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  exponentialDelay,
  handleMaxRetryTimesExceeded,
  handleRetry,
  setCurrentState,
  shouldRetry,
} from "./AxiosRetry";
import {
  DEVICE_ID_HEADER,
  getDeviceId,
  validateAndSetDeviceId,
} from "./deviceId";
import { logger } from "./logger";

const USER_SOURCE_KEY = "user_source";
const PORTAL_UNAUTHORIZED_PATH = "/auth/invalidsession/";

const apiVersionPrefix = "/api/v0";
const analyticsApiPrefix = `${apiVersionPrefix}/tracking`;
const analyticsEventApi = `${analyticsApiPrefix}/event`;

export interface ClientHintHeaders {
  "X-Accept-Currency"?: string;
  // https://wicg.github.io/responsive-image-client-hints/#sec-ch-width
  // Remove when sec-ch-width is released and supported
  "X-Sec-CH-Viewport-Width"?: number;
}

export interface Headers extends ClientHintHeaders {
  "Content-Type"?: string;
  "Hopper-Session"?: string;
  "Tenant-Session"?: string;
}

export interface AxiosInterceptorsProps {
  children?: ReactNode;
  trackingProperties: TrackingPropertiesType | undefined;
  /**
   * Experiment assignments that originate outside the feature-flags service.
   * Sent as `local_experiments` so tracking enrichment preserves them.
   */
  localTrackingProperties?: TrackingPropertiesType | undefined;
  additionalTrackingProperties?: TrackingPropertiesType | undefined;
  userSource?: string | null;
  userMedium?: string;
  userCampaign?: string;
  userContent?: string;
  isSignedIn?: boolean;
  isGuestUser?: boolean;
  delegatedTo?: string;
  tenant: string;
  locale: string;
  currency: string;
  requestHeaders: Headers;
  version: string;
  deviceType?: string;
  retryTransientErrors?: boolean;
  withCredentials?: boolean;
  logRequest?: (res: AxiosRequestConfig) => void;
  logResponse?: (res: AxiosResponse) => void;
  logError?: (error: AxiosError) => void;
  errorAction?: (error: AxiosError) => Promise<AxiosError | AxiosResponse>;
}

function addTrackingProperties(
  trackingProperties: TrackingPropertiesType | undefined,
  properties?: object,
  propertyName: "experiments" | "local_experiments" = "experiments"
): object {
  if (!properties) {
    properties = {};
  }
  if (trackingProperties) {
    properties[propertyName] = [
      ...(Array.isArray(properties[propertyName])
        ? properties[propertyName]
        : []),
      ...Object.keys(trackingProperties).map(
        (key: string) => `${key}_${trackingProperties[key]}`
      ),
    ];
  }

  return properties;
}

export const axiosInstance: AxiosInstance = axios.create();

// Wire the shared axios instance into core-tracking's batcher so it
// uses the same interceptor-enriched instance without a circular import.
setTrackingAxiosInstance(axiosInstance);

// We don't know the type of properties passed here from axios interceptors

const isReadonly = (obj: any, prop: string): boolean => {
  try {
    const originalValue = obj[prop];
    obj[prop] = originalValue; // Attempt to modify the property
    return false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return true;
  }
};

const unpackTrackingRequestData = (data: unknown) => {
  if (typeof data === "object" || typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch (e) {
    logger() && logger().error("Tracking data could not be parsed: ", e);
  }
  return data;
};

/**
 * Build the headers this interceptor adds to every API request.
 *
 * Also used for the tracking batcher's exit flush, which sends via `fetch` and
 * so never passes through here. `Content-Type` is left to the caller because
 * axios and `fetch` set it differently.
 */
export const buildRequestHeaders = (
  requestHeaders: Headers,
  locale: string
): Record<string, string> => {
  const headers: Record<string, string> = {};
  const deviceId = getDeviceId();

  if (deviceId) {
    headers[DEVICE_ID_HEADER] = deviceId;
  }

  if (locale) {
    headers[ACCEPT_LANGUAGE_HEADER] =
      getAcceptLanguagesFromLocale(locale).join(",");
  }

  Object.keys(requestHeaders).forEach((headerKey: string) => {
    const value = requestHeaders[headerKey];
    // Every field on `Headers` is optional; axios drops nullish values rather
    // than sending the literal string "undefined", so do the same here
    if (value != null) {
      headers[headerKey] = String(value);
    }
  });

  return headers;
};

const addRequestHeaders = (
  req: InternalAxiosRequestConfig,
  requestHeaders: Headers,
  locale: string
) => {
  req.headers["Content-Type"] = "application/json";

  const headers = buildRequestHeaders(requestHeaders, locale);
  Object.keys(headers).forEach((headerKey: string) => {
    req.headers.set(headerKey, headers[headerKey]);
  });
};

type TrackingRequestProperties = Pick<
  AxiosInterceptorsProps,
  | "delegatedTo"
  | "isGuestUser"
  | "isSignedIn"
  | "locale"
  | "currency"
  | "tenant"
  | "version"
  | "additionalTrackingProperties"
  | "localTrackingProperties"
  | "trackingProperties"
  | "userSource"
  | "userMedium"
  | "userCampaign"
  | "userContent"
  | "deviceType"
>;

/**
 * Build the tracking properties this interceptor adds on top of whatever the
 * caller already put on the event.
 *
 * Also used to snapshot the same properties for the tracking batcher's
 * exit flush, which sends via `fetch` and so never passes through here.
 */
export const buildTrackingProperties = (
  eventProperties: object | undefined,
  requestProperties: TrackingRequestProperties
) => {
  let properties: object = {
    ...eventProperties,
    ...getUserDeviceData(),
    device_type: requestProperties.deviceType ?? getDeviceData().type,
    is_agent_session: Boolean(requestProperties.delegatedTo),
    delegated_to: requestProperties.delegatedTo,
    guest_user: requestProperties.isGuestUser,
    signed_in: requestProperties.isSignedIn,
    locale: requestProperties.locale,
    currency: requestProperties.currency,
    tenant: requestProperties.tenant,
    // The core events uses this to build app_version
    //   Field(
    //     "", expr="_.split(' ', 1)[0] if _ else ''", coalesce=["device_app_version", "$app_version_string"]
    //   )
    // So we need to send $app_version_string which will be fed into app_version on amplitude
    // See ref: https://github.com/hopper-org/power-loader/blob/206e8e41259d5e01d122d9debb66b87da585a481/eventschema/core_traits.py#L75
    $app_version_string: requestProperties.version,
    preferred_languages: (navigator?.languages ?? []).join(","),
    referrer_url: document.referrer,
    referrer_url_host: document.referrer
      ? new URL(document.referrer).hostname
      : "",
  };
  if (requestProperties.additionalTrackingProperties) {
    properties = {
      ...properties,
      ...requestProperties.additionalTrackingProperties,
    };
  }
  if (requestProperties.trackingProperties) {
    properties = addTrackingProperties(
      requestProperties.trackingProperties,
      properties
    );
  }
  if (requestProperties.localTrackingProperties) {
    properties = addTrackingProperties(
      requestProperties.localTrackingProperties,
      properties,
      "local_experiments"
    );
  }

  if (requestProperties.userSource) {
    properties[USER_SOURCE_KEY] = requestProperties.userSource;
    properties["utm_source"] = requestProperties.userSource;
  }
  if (requestProperties.userMedium) {
    properties[USER_MEDIUM_KEY] = requestProperties.userMedium;
    properties["utm_medium"] = requestProperties.userMedium;
  }

  if (requestProperties.userCampaign) {
    properties[USER_CAMPAIGN_KEY] = requestProperties.userCampaign;
    properties["utm_campaign"] = requestProperties.userCampaign;
  }

  if (requestProperties.userContent) {
    properties[USER_CONTENT_KEY] = requestProperties.userContent;
    properties["utm_content"] = requestProperties.userContent;
  }

  return properties;
};

const addRequestProperties = (
  req: InternalAxiosRequestConfig,
  requestProperties: TrackingRequestProperties
) => {
  if (req.url !== analyticsEventApi) {
    return;
  }

  // We need to unpack the data here because the data may be stringified in the request
  req.data = unpackTrackingRequestData(req.data);
  const properties = buildTrackingProperties(
    req.data.properties,
    requestProperties
  );

  // Silence some console errors when properties are readonly for some reason
  if (!isReadonly(req.data, "properties")) {
    req.data.properties = properties;
  }
};

export const AxiosInterceptors = ({
  children,
  delegatedTo,
  isSignedIn,
  isGuestUser,
  tenant,
  locale,
  currency,
  trackingProperties,
  localTrackingProperties,
  additionalTrackingProperties,
  userSource,
  userMedium,
  userCampaign,
  userContent,
  deviceType,
  requestHeaders,
  version,
  retryTransientErrors = false,
  withCredentials = false,
  logRequest,
  logResponse,
  logError,
  errorAction,
}: AxiosInterceptorsProps) => {
  const navigate = useNavigate();

  const handleReq = useCallback(
    (req: InternalAxiosRequestConfig) => {
      if (!req?.url?.startsWith("/api")) {
        return req;
      }

      req.withCredentials = withCredentials;

      addRequestHeaders(req, requestHeaders, locale);

      addRequestProperties(req, {
        locale,
        delegatedTo,
        isGuestUser,
        isSignedIn,
        currency,
        tenant,
        version,
        additionalTrackingProperties,
        localTrackingProperties,
        trackingProperties,
        userSource,
        userMedium,
        userCampaign,
        userContent,
        deviceType,
      });

      if (req.url !== analyticsEventApi && logRequest) {
        logRequest(req);
      }
      return req;
    },
    [
      locale,
      requestHeaders,
      withCredentials,
      logRequest,
      delegatedTo,
      isGuestUser,
      isSignedIn,
      currency,
      tenant,
      version,
      additionalTrackingProperties,
      localTrackingProperties,
      trackingProperties,
      userSource,
      userMedium,
      userCampaign,
      userContent,
      deviceType,
    ]
  );

  const handleRes = useCallback(
    (res: AxiosResponse) => {
      if (!res.config?.url?.startsWith("/api")) {
        return res;
      }

      if (res.config.url !== analyticsEventApi && logResponse) {
        logResponse(res);
      }
      validateAndSetDeviceId(res.headers);
      return res;
    },
    [logResponse]
  );

  const handleError = useCallback(
    async (error: AxiosError) => {
      logError?.(error);

      // Don't handle tracking events errors to prevent ad block issues
      if (
        !error.request?.responseURL.includes("tracking/event") &&
        error.response?.status === 401
      ) {
        if (errorAction) {
          return errorAction(error);
        }
        navigate(PORTAL_UNAUTHORIZED_PATH, {
          state: {
            from: window.location.href,
            status: HttpStatusCode.Unauthorized,
          },
        });
      }

      // Handle retries
      const { config } = error;
      if (!config || !retryTransientErrors) {
        return Promise.reject(error);
      }
      const currentState = setCurrentState(config, {
        retries: 3,
        retryDelay: exponentialDelay,
        onRetry: () => {
          logger() && logger().info("Retrying request");
        },
        onMaxRetryTimesExceeded: () => {
          logger() && logger().error("Max retry reached");
        },
      });
      if (error.response && currentState.validateResponse?.(error.response)) {
        // no issue with response
        return error.response;
      }
      if (await shouldRetry(currentState, error)) {
        return handleRetry(axiosInstance, currentState, error, config);
      }

      await handleMaxRetryTimesExceeded(currentState, error);

      return Promise.reject(error);
    },
    [errorAction, logError, navigate, retryTransientErrors]
  );

  useEffect(() => {
    const requestInterceptor = {
      instance: axiosInstance.interceptors.request.use(handleReq),
      global: axios.interceptors.request.use(handleReq),
    };

    const responseInterceptor = {
      instance: axiosInstance.interceptors.response.use(handleRes, handleError),
      global: axios.interceptors.response.use(handleRes, handleError),
    };

    //runs when component unmount
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor.instance);
      axiosInstance.interceptors.response.eject(responseInterceptor.instance);
      axios.interceptors.request.eject(requestInterceptor.global);
      axios.interceptors.response.eject(responseInterceptor.global);
    };
  }, [
    delegatedTo,
    handleError,
    handleReq,
    handleRes,
    requestHeaders,
    tenant,
    trackingProperties,
    userSource,
  ]);

  // The tracking batcher's exit flush sends with `fetch`, so it never reaches
  // the interceptors above. Give it the same headers and properties they would
  // have added, resolved when it flushes so a late-arriving device id counts.
  useEffect(() => {
    setExitFlushEnricher((eventProperties) => ({
      headers: buildRequestHeaders(requestHeaders, locale),
      properties: buildTrackingProperties(eventProperties, {
        additionalTrackingProperties,
        currency,
        delegatedTo,
        deviceType,
        isGuestUser,
        isSignedIn,
        locale,
        localTrackingProperties,
        tenant,
        trackingProperties,
        userCampaign,
        userContent,
        userMedium,
        userSource,
        version,
      }) as Record<string, unknown>,
    }));

    return () => setExitFlushEnricher(null);
  }, [
    additionalTrackingProperties,
    currency,
    delegatedTo,
    deviceType,
    isGuestUser,
    isSignedIn,
    locale,
    localTrackingProperties,
    requestHeaders,
    tenant,
    trackingProperties,
    userCampaign,
    userContent,
    userMedium,
    userSource,
    version,
  ]);

  return <>{children}</>;
};
